import { createContext, useContext, useState, useCallback } from 'react';
import ToastContainer from '../components/ui/ToastContainer.jsx';

/**
 * 全域通知系統。
 *
 * 暴露：
 *   showToast(message, type?, duration?)
 *     - type: 'success' | 'error' | 'info' | 'warning'   (預設 'success')
 *     - duration: 自動消失毫秒數，0 = 不自動消失       (預設 4000)
 *   dismiss(id)         手動關閉某條訊息
 *
 * 用法：
 *   const { showToast } = useToast();
 *   showToast('已加入購物車');
 *   showToast('登入失敗', 'error');
 *
 * 設計：
 *   - Provider 只負責「list 狀態」與 show/dismiss API
 *   - 每條 Toast 自己管理 timer 與進出場動畫（見 ToastContainer）
 *   - <ToastContainer> 與 children 並列渲染，確保在 DOM 樹尾端，
 *     z-index 上自然蓋過其他元素
 */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast 必須在 <ToastProvider> 內使用');
  return ctx;
}
